/**
 * PortfolioSelector — Plus-only dropdown above the My Portfolio card.
 *
 * Round 92o++++++++N++++++++++ (May 2026).
 *
 * Lets paying users switch between their (up to five) named portfolio
 * groups + the combined "All Portfolios" view. The selection is
 * persisted on the user doc via `/api/portfolios/active`, so every
 * device / browser the user signs in from boots up to the same view.
 *
 * Free users: this component renders nothing (returns null) so the
 * Portfolio page is byte-for-byte identical to today.
 *
 * Admin (you): treated as Plus via the existing role-proxy.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ChevronDown, Plus, Pencil, Trash2, Layers, Check, X, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  getSnapshot, subscribe, hydrate, setActive, deletePortfolio, renamePortfolio,
} from '@/lib/portfolioStore';
import { getBroker } from '@/lib/brokers';
import { getAccountType } from '@/lib/accountTypes';
import { showUndoToast } from '@/lib/undoToast';
import CreatePortfolioModal from './CreatePortfolioModal';
import ReplacePortfolioModal from './ReplacePortfolioModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortfolioSelector({ onChange }) {
  const store = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // portfolio_id pending delete
  // Round 92o++++++++N++++++++++ (Jun 2026) — Inline rename state.
  // `editingId` is the portfolio_id whose row is currently in edit
  // mode. `editingName` is the in-flight value. Hover-pencil swaps a
  // row's static label for an input field; Enter or check-mark saves,
  // Escape or X cancels. Lets users rename the auto-migrated
  // "My Portfolio" or any other portfolio without leaving the
  // dropdown.
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const editInputRef = useRef(null);

  // Round P108z (Feb 2026) — persistent Undo pill. Poll active
  // (non-consumed, non-expired) portfolio_delete snapshots every 10s
  // so the pill above "+ New portfolio" reflects reality. Countdown
  // updates on a 1s tick locally so we don't hammer the backend.
  const [pendingUndos, setPendingUndos] = useState([]);
  const [nowMs, setNowMs] = useState(Date.now());
  const [replaceCtx, setReplaceCtx] = useState(null); // {snapId, restoreName, portfolios, currentCount, maxPortfolios}

  const fetchPending = async () => {
    try {
      const { data } = await axios.get(`${API}/portfolio/undo/pending`, { withCredentials: true });
      const rows = Array.isArray(data?.pending) ? data.pending : [];
      // Anchor a `deadline_ms` timestamp locally so our 1s tick can
      // drive the countdown without re-querying the server.
      const stamped = rows.map((r) => ({
        ...r,
        deadline_ms: Date.now() + (Number(r.seconds_remaining) || 0) * 1000,
      }));
      setPendingUndos(stamped);
    } catch {
      setPendingUndos([]);
    }
  };

  useEffect(() => {
    fetchPending();
    const poll = setInterval(fetchPending, 10_000);
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);

  useEffect(() => {
    hydrate();
  }, []);

  // Notify parent any time the active selection changes so the
  // Dashboard / PortfolioSummary can re-fetch with the new
  // ?portfolio_id= query param.
  useEffect(() => {
    if (!store.loaded) return;
    if (typeof onChange === 'function') onChange(store.activeId);
  }, [store.activeId, store.loaded, onChange]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest?.('[data-portfolio-selector-root]')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!store.loaded) return null;
  // Free tier never sees the dropdown. Their experience is unchanged.
  if (!store.isPlus) return null;

  const activePf = store.portfolios.find((p) => p.portfolio_id === store.activeId) || null;
  const isAllView = !store.activeId;
  const atCap = store.portfolios.length >= (store.caps?.max_portfolios || 5);

  const onSelect = async (pid) => {
    setOpen(false);
    if (pid === store.activeId) return;
    await setActive(pid);
    toast.success(pid ? `Switched to "${store.portfolios.find((p) => p.portfolio_id === pid)?.name || 'portfolio'}"` : 'Switched to All Portfolios');
    // Notify Dashboard / other consumers that the visible portfolio
    // changed so they re-fetch owned-tickers, charts, etc. We reuse
    // the existing global event so we don't have to plumb a new prop
    // through every dashboard descendant.
    try { window.dispatchEvent(new Event('portfolio-changed')); } catch { /* SSR */ }
  };

  const onDelete = async (pid) => {
    try {
      const result = await deletePortfolio(pid);
      const posN = Number(result?.positions_removed || 0);
      const txN = Number(result?.transactions_removed || 0);
      setConfirmDelete(null);
      // Round P108e (Feb 2026) — undo toast for portfolio delete.
      // Restores the group doc + positions + cash_transactions,
      // strictly scoped to this exact portfolio_id (backend enforces
      // — undo can never widen scope to sibling portfolios).
      if (result?.undo_snapshot_id) {
        showUndoToast(result, { onUndone: () => fetchPending() });
        // Also refresh our pending list immediately so the pill
        // appears without waiting for the 10s poll.
        fetchPending();
      } else {
        // Round 92o++++++++N++++++++++ (Jun 2026) — honest cascade
        // delete toast. Old copy said "positions are back in the All
        // view", which was incorrect after the cascade fix (positions
        // are now permanently removed, not untagged).
        const parts = [];
        if (posN > 0) parts.push(`${posN} position${posN === 1 ? '' : 's'}`);
        if (txN > 0) parts.push(`${txN} transaction${txN === 1 ? '' : 's'}`);
        const detail = parts.length
          ? ` Removed ${parts.join(' and ')}.`
          : ' (No positions or transactions were in this portfolio.)';
        toast.success(`Portfolio deleted.${detail}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete portfolio.');
    }
  };

  // Round P108z (Feb 2026) — click handler for the persistent Undo
  // pill. Fires the restore directly if we have capacity; opens the
  // ReplacePortfolioModal on 409, refreshes the pending list on 410
  // (expired) or after success.
  const onUndoPillClick = async (snap) => {
    try {
      const { data } = await axios.post(
        `${API}/portfolio/undo/${snap.snapshot_id}`,
        {},
        { withCredentials: true },
      );
      toast.success(
        `Restored "${data?.portfolio_name || snap.portfolio_name}"`,
        {
          description: `${data?.positions_restored || 0} position(s) and ${data?.transactions_restored || 0} transaction(s) put back exactly where they were.`,
        },
      );
      try { window.dispatchEvent(new Event('portfolio-changed')); } catch { /* SSR */ }
      try { window.dispatchEvent(new Event('cash-changed')); } catch { /* SSR */ }
      fetchPending();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && typeof detail === 'object' && detail?.code === 'portfolio_cap_reached') {
        // Open the picker modal.
        setReplaceCtx({
          snapId: snap.snapshot_id,
          restoreName: detail.restore_name || snap.portfolio_name,
          portfolios: detail.portfolios || [],
          currentCount: detail.current_count || 0,
          maxPortfolios: detail.max_portfolios || 5,
        });
        return;
      }
      if (status === 410) {
        toast.error('Undo window expired', {
          description: 'The 15-minute limit passed. Deleted data can no longer be restored automatically.',
        });
        fetchPending();
        return;
      }
      const msg = typeof detail === 'string' ? detail : detail?.message;
      toast.error('Undo failed', {
        description: msg || 'Try again — if it keeps failing, contact support.',
      });
    }
  };

  const startRename = (pf) => {
    setEditingId(pf.portfolio_id);
    setEditingName(pf.name || '');
    // Focus + select the input on the next tick once it's mounted.
    setTimeout(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }, 0);
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName('');
  };

  const commitRename = async (pid) => {
    const trimmed = (editingName || '').trim();
    const current = store.portfolios.find((p) => p.portfolio_id === pid);
    if (!trimmed) {
      toast.error('Portfolio name cannot be empty.');
      return;
    }
    if (trimmed === current?.name) {
      cancelRename();
      return;
    }
    try {
      await renamePortfolio(pid, { name: trimmed });
      toast.success(`Renamed to "${trimmed}".`);
      cancelRename();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not rename portfolio.');
    }
  };

  return (
    <div className="relative mb-3" data-portfolio-selector-root data-testid="portfolio-selector">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 rounded-lg border-2 border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-950/30 hover:bg-violet-100/60 dark:hover:bg-violet-950/50 px-4 py-2.5 transition-colors"
        data-testid="portfolio-selector-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Layers className="w-4 h-4 text-violet-600 dark:text-violet-400 shrink-0" />
          <div className="flex flex-col items-start min-w-0">
            <span className="text-[10px] uppercase tracking-wider font-bold text-violet-600 dark:text-violet-400">
              Viewing
            </span>
            <span className="text-sm font-bold truncate max-w-[60vw] sm:max-w-none" data-testid="portfolio-selector-current">
              {isAllView ? 'All Portfolios' : (activePf?.name || '—')}
            </span>
          </div>
          {!isAllView && activePf && (
            <PortfolioMetaChip portfolio={activePf} />
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-violet-600 dark:text-violet-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full max-h-[420px] overflow-y-auto rounded-lg border bg-popover shadow-xl"
          data-testid="portfolio-selector-menu"
        >
          {/* All view — always first row */}
          <MenuRow
            active={isAllView}
            onClick={() => onSelect(null)}
            testId="portfolio-selector-option-all"
          >
            <span className="font-bold">All Portfolios</span>
            <span className="text-[11px] text-muted-foreground">
              {store.allView?.ticker_count || 0} ticker{(store.allView?.ticker_count || 0) === 1 ? '' : 's'} · combined view
            </span>
          </MenuRow>
          <div className="border-t" />

          {store.portfolios.length === 0 ? (
            <p className="px-3 py-3 text-xs text-muted-foreground text-center">
              No named portfolios yet. Create one to split holdings by broker / account.
            </p>
          ) : (
            store.portfolios.map((p) => (
              <div key={p.portfolio_id} className="group relative">
                {editingId === p.portfolio_id ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-violet-50/60 dark:bg-violet-950/40" data-testid={`portfolio-selector-edit-row-${p.portfolio_id}`}>
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commitRename(p.portfolio_id); }
                        if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                      }}
                      maxLength={60}
                      placeholder="Portfolio name"
                      className="flex-1 min-w-0 px-2 py-1 text-sm font-bold bg-background border-2 border-violet-300 dark:border-violet-700 rounded focus:outline-none focus:border-violet-500"
                      data-testid={`portfolio-selector-edit-input-${p.portfolio_id}`}
                    />
                    <button
                      type="button"
                      onClick={() => commitRename(p.portfolio_id)}
                      className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-950/40 text-green-600 dark:text-green-400"
                      title="Save name"
                      data-testid={`portfolio-selector-edit-save-${p.portfolio_id}`}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={cancelRename}
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground"
                      title="Cancel"
                      data-testid={`portfolio-selector-edit-cancel-${p.portfolio_id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <MenuRow
                      active={p.portfolio_id === store.activeId}
                      onClick={() => onSelect(p.portfolio_id)}
                      testId={`portfolio-selector-option-${p.portfolio_id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-bold truncate">{p.name}</span>
                        <PortfolioMetaChip portfolio={p} />
                      </div>
                      <span className="text-[11px] text-muted-foreground">
                        {p.ticker_count || 0} / {store.caps?.max_tickers_per_portfolio || 500} tickers
                      </span>
                    </MenuRow>
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); startRename(p); }}
                        className="flex items-center gap-1 px-2 py-1.5 rounded hover:bg-violet-100 dark:hover:bg-violet-950/40 text-violet-600 dark:text-violet-400 text-[11px] font-semibold"
                        title="Rename portfolio"
                        data-testid={`portfolio-selector-rename-${p.portfolio_id}`}
                      >
                        Rename <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-muted-foreground text-xs">/</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(p.portfolio_id); }}
                        className="flex items-center gap-1 px-2 py-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-400 text-[11px] font-semibold"
                        title="Delete portfolio (positions stay in your All view)"
                        data-testid={`portfolio-selector-delete-${p.portfolio_id}`}
                      >
                        Delete <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}

          <div className="border-t" />
          {/* Round P108z (Feb 2026) — persistent Undo Delete pill.
              One row per active portfolio_delete snapshot with a
              countdown to expiry. Click → restore. If at cap, the
              backend returns 409 and we open ReplacePortfolioModal. */}
          {pendingUndos
            .filter((p) => (p.deadline_ms - nowMs) > 0)
            .map((p) => {
              const secsLeft = Math.max(0, Math.floor((p.deadline_ms - nowMs) / 1000));
              const mm = String(Math.floor(secsLeft / 60)).padStart(2, '0');
              const ss = String(secsLeft % 60).padStart(2, '0');
              return (
                <button
                  key={p.snapshot_id}
                  type="button"
                  onClick={() => onUndoPillClick(p)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-amber-50/60 dark:hover:bg-amber-950/30 border-b border-amber-200/60 dark:border-amber-800/40 bg-amber-50/30 dark:bg-amber-950/15 transition-colors"
                  data-testid={`undo-pill-${p.snapshot_id}`}
                  title={`Restore "${p.portfolio_name}" with all ${p.positions_count} position(s) and ${p.transactions_count} transaction(s).`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Undo2 className="w-3.5 h-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <span className="text-xs font-semibold text-amber-800 dark:text-amber-200 truncate">
                      Undo delete: &ldquo;{p.portfolio_name}&rdquo;
                    </span>
                  </span>
                  <span className="text-[10px] font-mono font-semibold text-amber-700 dark:text-amber-300 tabular-nums shrink-0">
                    {mm}:{ss}
                  </span>
                </button>
              );
            })}
          <button
            type="button"
            onClick={() => { setOpen(false); setCreateOpen(true); }}
            disabled={atCap}
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-muted/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="portfolio-selector-create"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
              <Plus className="w-4 h-4" /> New portfolio
            </span>
            <span className="text-[11px] text-muted-foreground">
              {store.portfolios.length} / {store.caps?.max_portfolios || 5}
            </span>
          </button>
        </div>
      )}

      {confirmDelete && (
        <div className="absolute z-[60] inset-x-0 top-full mt-1 rounded-lg border-2 border-rose-400 bg-rose-50 dark:bg-rose-950 p-3 shadow-xl" data-testid="portfolio-selector-confirm-delete">
          <p className="text-xs font-semibold text-rose-800 dark:text-rose-200 mb-2">
            Delete this portfolio? <span className="font-bold uppercase">All</span> positions and transactions inside it will be permanently removed. This can&rsquo;t be undone.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="flex-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-muted hover:bg-muted/70"
              data-testid="portfolio-selector-cancel-delete"
            >Cancel</button>
            <button
              type="button"
              onClick={() => onDelete(confirmDelete)}
              className="flex-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700"
              data-testid="portfolio-selector-confirm-delete-btn"
            >Delete &amp; wipe data</button>
          </div>
        </div>
      )}

      <CreatePortfolioModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      {/* Round P108z (Feb 2026) — cap-full replacement picker.
          Opens when the persistent Undo pill hits 409 because the
          user is already at 5/5. On success we refresh the pending
          list so the pill disappears. */}
      <ReplacePortfolioModal
        open={!!replaceCtx}
        onClose={() => setReplaceCtx(null)}
        snapshotId={replaceCtx?.snapId}
        restoreName={replaceCtx?.restoreName}
        portfolios={replaceCtx?.portfolios || []}
        currentCount={replaceCtx?.currentCount || 0}
        maxPortfolios={replaceCtx?.maxPortfolios || 5}
        onRestored={() => { fetchPending(); setReplaceCtx(null); }}
      />
    </div>
  );
}

function MenuRow({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors ${
        active ? 'bg-violet-50 dark:bg-violet-950/30' : ''
      }`}
    >
      <div className="flex flex-col items-start gap-0.5 min-w-0">
        {children}
      </div>
    </button>
  );
}

function PortfolioMetaChip({ portfolio }) {
  const broker = getBroker(portfolio.broker);
  const acct = getAccountType(portfolio.account_type);
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted/50 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground shrink-0">
      {broker ? <span>{broker.short}</span> : null}
      {broker && acct ? <span className="opacity-60">·</span> : null}
      {acct ? <span>{acct.short}</span> : null}
    </span>
  );
}

// Re-export the active-id hook so consumers like PortfolioSummary
// can subscribe without each rewriting the useSyncExternalStore line.
export function useActivePortfolioId() {
  const store = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { activeId: store.activeId, loaded: store.loaded, isPlus: store.isPlus };
}
